import { SensorParameterConfigBase, SensorParameterValueType } from "../../SensorConfigurationTypes.js";
import { sensorParameterNumericValidators, combineValidators } from "../../SensorConfigurationParameterHelpers.js";
import { DirectSubChannel, SensorPortCommunicationMode } from "../../../portConfigs.js";
import { SensorClusterConfig } from "../types.js";

const genericFlowmeterParams = {
    kFactor: {
        key: "kFactor",
        type: SensorParameterValueType.NUMBER,
        name: "K-Factor",
        description: "Volume per pulse (liters per pulse). Provided on the sensor's calibration sheet.",
        defaultValue: 1,
        validator: combineValidators(
            sensorParameterNumericValidators.isNumber,
            sensorParameterNumericValidators.isGreaterThanZero,
        ),
        mustBeNonNegative: true,
        mustBeInteger: false,
        parameterUnitCategory: "flowmeterKFactor",
    },
    frequencyOffset: {
        key: "frequencyOffset",
        type: SensorParameterValueType.NUMBER,
        name: "Frequency Offset",
        description:
            "Offset added to the raw pulse frequency before applying the K-factor (Hz). Use 0 if the sensor has no frequency offset.",
        defaultValue: 0,
        validator: sensorParameterNumericValidators.isNumber,
        mustBeNonNegative: false,
        mustBeInteger: false,
        parameterUnitCategory: "flowmeterOffset",
    },
    minimumFrequency: {
        key: "minimumFrequency",
        type: SensorParameterValueType.NUMBER,
        name: "Minimum Frequency",
        description:
            "Pulse frequency at or below which the flow reading is forced to zero (Hz). Recommended to avoid reporting phantom flow at low frequencies.",
        defaultValue: 0,
        validator: combineValidators(
            sensorParameterNumericValidators.isNumber,
            sensorParameterNumericValidators.isGreaterThanOrEqualToZero,
        ),
        mustBeNonNegative: true,
        mustBeInteger: false,
        parameterUnitCategory: "flowmeterMinimumFrequency",
    },
} as const satisfies Record<string, SensorParameterConfigBase<number>>;

export const genericFlowmeterClusterConfigs: Record<string, SensorClusterConfig<typeof genericFlowmeterParams>> = {
    genericFlowmeter: {
        communicationMode: SensorPortCommunicationMode.DIRECT,
        requiredSubChannels: [DirectSubChannel.PULSE_FREQUENCY],
        optionalSubChannels: [],
        isPermanentlyAttached: false,
        parameterConfigs: genericFlowmeterParams,
        manufacturerName: "Unknown",
        modelName: "N/A",
        friendlyName: "Generic Flowmeter",
        friendlyShortName: "Flowmeter",
        generateSensorElements: ({ kFactor, frequencyOffset, minimumFrequency }) => [
            {
                sensorElementConfigKey: "flowmeter",
                parameters: { kFactor, frequencyOffset, minimumFrequency },
                legalConvertedRange: [0, null],
                subChannel: DirectSubChannel.PULSE_FREQUENCY,
            },
        ],
        migrationProperties: {
            legacySensorType: "genericFlowmeter",
            legacyParametersToClusterParameters: (sensorParams) => ({
                kFactor: sensorParams.litersPerPulse as number,
                frequencyOffset: sensorParams.offset as number,
                minimumFrequency: sensorParams.minValue as number,
            }),
            clusterParametersToLegacyParameters: (clusterParameters) => ({
                litersPerPulse: clusterParameters.kFactor,
                offset: clusterParameters.frequencyOffset,
                minValue: clusterParameters.minimumFrequency,
            }),
        },
    },
};
